import React from "react";
import { Link } from "react-router";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@mindstonehq/ui";
import type {
  CommunityTalkItem,
  CommunityTalksSignal,
  CommunityTalkTestimonial,
} from "~/lib/community-signals-types";
import {
  deriveTalkActions,
  formatEventMeta,
  splitFeaturedTalks,
} from "~/lib/profile-celebration";

function talkKey(talk: CommunityTalkItem): string {
  return `${talk.eventId}:${talk.talkId}`;
}

function TalkEventLink({ talk }: { talk: CommunityTalkItem }) {
  if (!talk.eventSlug) {
    return <span>{talk.eventName}</span>;
  }

  return (
    <Link
      to={`/events/${talk.eventSlug}`}
      className="font-medium text-foreground hover:underline"
    >
      {talk.eventName}
    </Link>
  );
}

function TalkTitle({ talk }: { talk: CommunityTalkItem }) {
  const [primaryAction] = deriveTalkActions(talk);
  const className = "underline decoration-border underline-offset-4 hover:decoration-foreground";

  if (!primaryAction) return <span>{talk.title}</span>;

  if (primaryAction.external) {
    return (
      <a
        href={primaryAction.href}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {talk.title}
      </a>
    );
  }

  return (
    <Link to={primaryAction.href} className={className}>
      {talk.title}
    </Link>
  );
}

function RatingBadge({ rating }: { rating: number }) {
  const label = `Average attendee rating ${rating.toFixed(1)} out of 5`;

  return (
    <Badge variant="secondary" aria-label={label}>
      ★ {rating.toFixed(1)}
    </Badge>
  );
}

function testimonialAttribution(
  testimonial: CommunityTalkTestimonial,
): string {
  if (testimonial.attribution === "named" && testimonial.authorName) {
    return `- ${testimonial.authorName}`;
  }
  return "- Attendee testimonial";
}

function TalkTestimonial({ talk }: { talk: CommunityTalkItem }) {
  const [testimonial, ...additional] = talk.testimonials ?? [];
  if (!testimonial) return null;

  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <blockquote className="border-l-2 border-primary/20 pl-3 text-sm italic text-muted-foreground">
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 pl-3 text-xs text-muted-foreground">
        <span>{testimonialAttribution(testimonial)}</span>
        {additional.length > 0 && (
          <span>+{additional.length} more testimonials</span>
        )}
      </div>
    </div>
  );
}

function TalkActions({ talk }: { talk: CommunityTalkItem }) {
  const actions = deriveTalkActions(talk);
  if (actions.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
      {actions.map((action) => {
        if (action.external) {
          return (
            <Button
              key={action.key}
              size="sm"
              variant={action.key === "watch" ? "default" : "outline"}
              className="w-full sm:w-auto"
              asChild
            >
              <a href={action.href} target="_blank" rel="noreferrer">
                {action.label}
                <ArrowTopRightOnSquareIcon className="size-3.5" />
              </a>
            </Button>
          );
        }

        return (
          <Button
            key={action.key}
            size="sm"
            variant={action.key === "watch" ? "default" : "outline"}
            className="w-full sm:w-auto"
            asChild
          >
            <Link to={action.href}>{action.label}</Link>
          </Button>
        );
      })}
    </div>
  );
}

function FeaturedTalkCard({ talk }: { talk: CommunityTalkItem }) {
  const meta = formatEventMeta(talk.city, talk.date);

  return (
    <Card className="gap-4 p-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground">
            <TalkTitle talk={talk} />
          </h3>
          {typeof talk.avgRating === "number" && (
            <RatingBadge rating={talk.avgRating} />
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          <TalkEventLink talk={talk} />
          {meta && <span className="ml-1">· {meta}</span>}
        </div>
      </div>

      <TalkTestimonial talk={talk} />
      <TalkActions talk={talk} />
    </Card>
  );
}

function AdditionalTalkRow({ talk }: { talk: CommunityTalkItem }) {
  const meta = formatEventMeta(talk.city, talk.date);
  const watchAction = deriveTalkActions(talk).find(
    (action) => action.key === "watch",
  );

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="line-clamp-2 text-sm font-medium text-foreground">
            <TalkTitle talk={talk} />
          </p>
          {typeof talk.avgRating === "number" && (
            <RatingBadge rating={talk.avgRating} />
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          <TalkEventLink talk={talk} />
          {meta && <span className="ml-1">· {meta}</span>}
        </div>
      </div>
      {watchAction && (
        <Button size="sm" variant="ghost" className="px-0 sm:px-3" asChild>
          <a href={watchAction.href} target="_blank" rel="noreferrer">
            {watchAction.label}
            <ArrowTopRightOnSquareIcon className="size-3.5" />
          </a>
        </Button>
      )}
    </li>
  );
}

function TalksPromptCard() {
  return (
    <Card className="bg-muted/30" id="profile-talks">
      <CardHeader>
        <CardTitle className="text-base">Talks portfolio</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        Your talks will appear here when they are connected to a public
        Mindstone event.
      </CardContent>
    </Card>
  );
}

export default function ProfileTalksPortfolio({
  talks,
  isCurrentUser,
}: {
  talks?: CommunityTalksSignal;
  isCurrentUser: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const { featured, additional } = splitFeaturedTalks(talks);

  if (featured.length === 0 && additional.length === 0) {
    return isCurrentUser ? <TalksPromptCard /> : null;
  }

  return (
    <Card id="profile-talks">
      <CardHeader>
        <CardTitle className="text-base">Talks portfolio</CardTitle>
        <CardDescription>
          Public talks and demos connected to Mindstone events.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-4">
          {featured.map((talk) => (
            <FeaturedTalkCard key={talkKey(talk)} talk={talk} />
          ))}
        </div>

        {additional.length > 0 && (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-fit px-0">
                {expanded
                  ? "Hide older talks"
                  : `Show ${additional.length} more talks`}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 flex flex-col gap-2">
                {additional.map((talk) => (
                  <AdditionalTalkRow key={talkKey(talk)} talk={talk} />
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
